import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import apiClient from '../apiClient';

const McpLoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Extract OAuth parameters from URL
  const state = searchParams.get('state');
  const redirectUri = searchParams.get('redirect_uri');
  const codeChallenge = searchParams.get('code_challenge');
  const codeChallengeMethod = searchParams.get('code_challenge_method');
  const clientId = searchParams.get('client_id');

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Validate required parameters on mount
  useEffect(() => {
    if (!state || !redirectUri) {
      setError('Invalid OAuth request. Missing required parameters.');
    }
  }, [state, redirectUri]);

  const formatPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('61')) {
      return '+' + cleaned;
    }
    if (cleaned.startsWith('0')) {
      return '+61' + cleaned.slice(1);
    }
    if (cleaned.startsWith('4')) {
      return '+61' + cleaned;
    }
    return '+' + cleaned;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!state || !redirectUri) {
      setError('Invalid OAuth request. Missing required parameters.');
      return;
    }

    const formattedPhone = formatPhoneNumber(phone);
    if (!formattedPhone || formattedPhone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      // Call core API /auth/mcp-login endpoint
      const response = await apiClient.post('/auth/mcp-login', {
        state,
        phone: formattedPhone,
        password,
      });
      // Navigate to the redirect URL returned by the API
      if (response.redirect_url) {
        window.location.href = response.redirect_url;
      }
    } catch (err) {
      if (err.status === 401) {
        setError('Incorrect phone number or password');
      } else if (err.status === 400) {
        setError(err.message || 'Invalid request');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            Sign in to Labourando
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Authorize <strong>{clientId || 'your AI assistant'}</strong> to access your account
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="Phone Number"
            placeholder="04XX XXX XXX"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            sx={{ mb: 2.5 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              },
            }}
            autoComplete="tel"
            autoFocus
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 1 }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            autoComplete="current-password"
            disabled={loading}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !phone || !password}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
              py: 1.5,
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In & Authorize'}
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            By signing in, you authorize the application to access your Labourando account
            via the MCP protocol for job posting and application management.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default McpLoginPage;